import {Segmented} from "antd";
import React from "react";
import {Chip, ChipType, Colors, Divider} from "../../../../ui";
import {Label} from "../../account";
import {PaymentMethodSelector} from "./PaymentMethodSelector";
import {DEFAULT_PAYMENT_METHODS_ORDER, PaymentMethodOption} from "./utils";

type Props = {
  selected: PaymentMethodOption;
  onSelect: (method: PaymentMethodOption) => void;
  isAgent?: boolean;
  achAllowed?: boolean;
  showAch?: boolean;
  cardDetailsTitle: string;
  applePayEligible?: boolean;
  merchantSiteId?: string;
  onChangeTab: () => void;
  achChip?: {label: string; variant: ChipType["variant_"]} | null;
  methods?: PaymentMethodOption[];
  achBlockedAfterFailure?: boolean;
};

type SegmentedOption = {label: React.ReactNode; value: PaymentMethodOption; disabled?: boolean};

const getDefaultAchChip = (achAllowed: boolean): {label: string; variant: ChipType["variant_"]} | null => {
  if (achAllowed) {
    return {label: "Popular", variant: "secondary"};
  }
  return null;
};

const PaymentMethodSelectorWrapper = ({
  selected,
  onSelect,
  isAgent = false,
  achAllowed = false,
  showAch = true,
  cardDetailsTitle,
  applePayEligible = false,
  merchantSiteId,
  onChangeTab,
  achChip,
  methods = DEFAULT_PAYMENT_METHODS_ORDER,
  achBlockedAfterFailure = false,
}: Props) => {
  const resolvedAchChip = achChip !== undefined ? achChip : getDefaultAchChip(achAllowed);

  if (!!applePayEligible) {
    return (
      <PaymentMethodSelector
        selected={selected}
        onSelect={onSelect}
        isAgent={isAgent}
        achAllowed={achAllowed}
        showAch={showAch}
        cardDetailsTitle={cardDetailsTitle}
        applePayEligible={applePayEligible}
        merchantSiteId={merchantSiteId}
        achChip={resolvedAchChip}
        methods={methods}
        achBlockedAfterFailure={achBlockedAfterFailure}
      />
    );
  }
  const chipData = resolvedAchChip;
  const label = chipData?.label ?? null;
  const variant = chipData?.variant ?? null;

  const segmentedOptions = methods.flatMap<SegmentedOption>((method) => {
    if (method === "ACH" && showAch) {
      return [
        {
          label: (
            <Label label={"ACH"}>
              {label && variant && <Chip label={label} variant_={variant as ChipType["variant_"]} />}
            </Label>
          ),
          value: method,
          disabled: achBlockedAfterFailure,
        },
      ];
    }
    if (method === "DebitCard") {
      return [{label: <Label label={cardDetailsTitle} />, value: method}];
    }
    return [];
  });

  if (segmentedOptions.length < 2) {
    return null;
  }

  return (
    <>
      <Divider sx={{height: "1px", width: "100%"}} />
      <Segmented
        value={selected}
        onChange={onChangeTab}
        size={"large"}
        style={{padding: "4px", width: "100%", backgroundColor: Colors.tertiary._200}}
        options={segmentedOptions}
        block
      />
    </>
  );
};

export {PaymentMethodSelectorWrapper};
